// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { OrderSyncSignal } from '@/components/order-sync-signal';
import { resyncFulfillmentOrder } from '@/fulfillment/api/resync-fulfillment-order';
import { type FulfillmentOrderStatus } from '@/fulfillment/constants/fulfillment-order-status';
import {
  fulfillmentDetailQueryKey,
  fulfillmentsQueryKey,
} from '@/fulfillment/query-keys';
import { DateTime } from 'luxon';

interface Props {
  fulfillmentKey: string;
  // The FO number, used to estimate resync time from its shipments
  fulfillmentNumber: string;
  status: FulfillmentOrderStatus;
  isClosed: boolean;
  // Hardfin's last poll of the 3PL for this order
  lastSyncedAt: DateTime | null;
  // The 3PL's last reported data update for this order
  lastThirdPartyUpdateAt: DateTime | null;
  // Whether the 3PL binding is currently in an error state
  hasSyncError: boolean;
  // The latest 3PL sync error text (empty when there is none)
  syncErrorMessage: string;
  // Open quarantined serials on this order
  quarantineCount?: number;
}

/**
 * 3PL sync-status signal for a fulfillment order: the shared OrderSyncSignal
 * wired to the fulfillment resync API and query keys. The order is terminal
 * once closed, and "Sent to 3PL" applies while it awaits shipment.
 */
export function FulfillmentSyncSignal(props: Props) {
  // "Sent to 3PL" applies only while the order is still awaiting shipment; once
  // shipment activity begins (or the order closes) it reads "Synced from 3PL".
  const isAwaiting =
    !props.isClosed &&
    (props.status === 'AWAITING_3PL' || props.status.startsWith('OPEN_'));

  return (
    <OrderSyncSignal
      orderType="fulfillment"
      orderKey={props.fulfillmentKey}
      orderNumber={props.fulfillmentNumber}
      isTerminal={props.isClosed}
      isAwaiting={isAwaiting}
      lastSyncedAt={props.lastSyncedAt}
      lastThirdPartyUpdateAt={props.lastThirdPartyUpdateAt}
      hasSyncError={props.hasSyncError}
      syncErrorMessage={props.syncErrorMessage}
      quarantineCount={props.quarantineCount}
      resyncOrder={resyncFulfillmentOrder}
      detailQueryKey={fulfillmentDetailQueryKey(props.fulfillmentKey)}
      listQueryKey={fulfillmentsQueryKey}
    />
  );
}
